/**
 * Provides a context for managing state and selectors related to Frost Probe line graph visualization.
 * @module FrostProbeContext
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DATA_RANGE, STATE_STATUS } from '@awp/constants';

import {
  fetchData,
  selectHeatMapObservation,
  selectStatus,
  selectWeatherStation,
  selectWeatherStationId,
} from '@stores/dataVisualizationSlice';

/**
 * Context for managing state and selectors related to multi-station data visualization.
 * @type {React.Context}
 */
const FrostProbeContext = createContext();

/**
 * Provider component for FrostProbeContext.
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - Child components wrapped by the provider.
 * @returns {React.ReactNode} Provider component.
 */
export const FrostProbeContextProvider = ({ children }) => {
  const dispatch = useDispatch();

  // State level values used in frost probe context
  const [dataRange, setDataRange] = useState({ endDate: new Date().getTime(), displayRange: DATA_RANGE.DEFAULT });
  const [loading, setLoading] = useState(false);
  const [observationTime, setObservationTime] = useState(12);

  // Data visualization selector variables used in frost probe context
  const status = useSelector(selectStatus);
  const weatherStationId = useSelector(selectWeatherStationId);
  const weatherStation = useSelector(selectWeatherStation);
  const heatMapObservation = useSelector(selectHeatMapObservation);

  const setEndDate = (dateValue) => {
    setDataRange({ endDate: Math.floor(dateValue), displayRange: dataRange.displayRange });
  };

  const updateDataRange = (dateValue, displayRange) => {
    setDataRange({ endDate: Math.floor(dateValue), displayRange: displayRange });
  };

  useEffect(() => {
    dispatch(fetchData({ dataRange: dataRange }));
  }, [weatherStationId, dataRange]);

  useEffect(() => {
    // BUG: https://moti-imb.atlassian.net/browse/WEAV-15883
    // This causes a short loading period so the chart is re-rendered when the observationTime changes
    setLoading(true);

    setTimeout(() => {
      setLoading(false);
    });
  }, [observationTime]);

  useEffect(() => {
    // true when loading
    setLoading(status.data === STATE_STATUS.LOADING || status.additionalData === STATE_STATUS.LOADING);
  }, [status]);

  // Structure the context values for the provider
  const stateValues = {
    dataRange,
    observationTime,
    loading,
  };

  const stateSetters = {
    setEndDate,
    updateDataRange,
    setObservationTime,
  };

  const selectorValues = {
    status,
    weatherStationId,
    weatherStation,
    heatMapObservation,
  };

  const memoizedValues = {};

  // Combine values into a single context value
  const contextValue = {
    ...stateValues,
    ...stateSetters,
    ...selectorValues,
    ...memoizedValues,
  };

  return <FrostProbeContext.Provider value={contextValue}>{children}</FrostProbeContext.Provider>;
};

/**
 * Hook for consuming FrostProbeContext.
 * @returns {object} Context value.
 */
export const useFrostProbeContext = () => useContext(FrostProbeContext);
